/**
 * RunTimeline — Visual run progression for a theatre.
 *
 * Cycle 063 Sprint 0: Shows run history as a compact timeline.
 * Each run is a dot on the timeline with status color, score, and duration.
 * Click to select a run for inspection.
 *
 * Future extension (noted in sprint plan): selection state designed
 * so a future sprint can connect it to canvas node badges for
 * time-travel view of graph state at a specific run.
 */

import { useMemo } from 'react';
import { CheckCircle2, XCircle, Clock, Ban, Loader2 } from 'lucide-react';
import type { EnvironmentRun } from '../../types/environmentRun';

interface RunTimelineProps {
  runs: EnvironmentRun[];
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
}

// Exported (sprint-237) so LineageTrack/LineageTimelineStrip reuse the
// canonical run-status palette instead of forking it. Component untouched.
export const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'var(--e-green-600)',
  FAILED: 'var(--e-red-600)',
  CANCELLED: 'var(--e-text-muted)',
  QUEUED: 'var(--e-text-muted)',
  INITIALISING: 'var(--e-amber-400)',
  INGESTING: 'var(--e-amber-400)',
  EVALUATING: 'var(--e-amber-400)',
  SETTLING: 'var(--e-amber-400)',
  CERTIFYING: 'var(--e-amber-400)',
};

const ACTIVE_STATUSES = new Set([
  'QUEUED', 'INITIALISING', 'INGESTING', 'EVALUATING', 'SETTLING', 'CERTIFYING',
]);

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function formatAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function StatusIcon({ status }: { status: string }) {
  const cls = 'h-3 w-3 flex-shrink-0';
  if (status === 'COMPLETED') return <CheckCircle2 className={cls} style={{ color: STATUS_COLORS.COMPLETED }} />;
  if (status === 'FAILED') return <XCircle className={cls} style={{ color: STATUS_COLORS.FAILED }} />;
  if (status === 'CANCELLED') return <Ban className={cls} style={{ color: STATUS_COLORS.CANCELLED }} />;
  if (ACTIVE_STATUSES.has(status)) return <Loader2 className={`${cls} animate-spin`} style={{ color: STATUS_COLORS.EVALUATING }} />;
  return <Clock className={cls} style={{ color: STATUS_COLORS.QUEUED }} />;
}

export function RunTimeline({ runs, selectedRunId, onSelectRun }: RunTimelineProps) {
  // Sort runs newest first
  const sorted = useMemo(
    () => [...runs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [runs],
  );

  if (sorted.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[10px] text-[var(--e-text-muted)]">
        No runs yet. Execute a run to see the timeline.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {sorted.map((run, idx) => {
        const isSelected = run.run_id === selectedRunId;
        const isActive = ACTIVE_STATUSES.has(run.status);

        return (
          <button
            key={run.run_id}
            type="button"
            onClick={() => onSelectRun(run.run_id)}
            className={`group relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors ${
              isSelected
                ? 'bg-[var(--e-purple-500)]/10 border border-[var(--e-purple-500)]/30'
                : 'hover:bg-[var(--e-bg-hover)] border border-transparent'
            }`}
          >
            {/* Timeline connector line */}
            {idx < sorted.length - 1 && (
              <div
                className="absolute left-[17px] top-[22px] w-px"
                style={{
                  height: 'calc(100% + 2px)',
                  background: 'var(--e-border-secondary)',
                  opacity: 0.4,
                }}
              />
            )}

            {/* Status dot */}
            <div className="relative z-[1] flex-shrink-0">
              <StatusIcon status={run.status} />
            </div>

            {/* Run info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-[var(--e-text-primary)] truncate">
                  Run {run.run_id.slice(0, 8)}
                </span>
                {isActive && (
                  <span className="text-[7px] font-mono uppercase tracking-wider text-[var(--e-amber-400)]">
                    {run.current_stage ?? run.status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[8px] font-mono text-[var(--e-text-muted)]">
                  {formatAge(run.created_at)}
                </span>
                {run.duration_ms !== null && (
                  <span className="text-[8px] font-mono text-[var(--e-text-muted)]">
                    {formatDuration(run.duration_ms)}
                  </span>
                )}
                {run.composite_score !== null && (
                  <span className="text-[8px] font-mono text-[var(--e-text-secondary)]">
                    {run.composite_score.toFixed(2)}
                  </span>
                )}
                {run.brier_score !== null && (
                  <span className="text-[8px] font-mono text-[var(--e-text-muted)]">
                    B:{run.brier_score.toFixed(3)}
                  </span>
                )}
              </div>
            </div>

            {/* Score trend indicator (compared to previous run) */}
            {run.composite_score !== null && idx < sorted.length - 1 && sorted[idx + 1].composite_score !== null && (
              <ScoreTrend current={run.composite_score} previous={sorted[idx + 1].composite_score!} />
            )}
          </button>
        );
      })}
    </div>
  );
}

function ScoreTrend({ current, previous }: { current: number; previous: number }) {
  const delta = current - previous;
  if (Math.abs(delta) < 0.001) return null;

  const positive = delta > 0;
  return (
    <span
      className="text-[8px] font-mono flex-shrink-0"
      style={{ color: positive ? 'var(--e-green-600)' : 'var(--e-red-600)' }}
    >
      {positive ? '+' : ''}{delta.toFixed(3)}
    </span>
  );
}
